import React, { useEffect, useState, type FormEvent } from 'react';
import { Check, Eye, EyeOff, Loader2, X } from 'lucide-react';
import type { ProfileDTO } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Input, Label } from '../../components/ui/Input';
import { cn } from '../../components/ui/cn';
import { t, useLanguage } from '../../lib/i18n';
import { checkPasswordRules, isPasswordStrong, PASSWORD_RULE_IDS, passwordRuleLabel } from './passwordRules';

interface ChangePasswordDialogProps {
  open: boolean;
  /** Hesabın şifresi yoksa (yalnızca Google ile açılmışsa) mevcut şifre sorulmaz. */
  hasPassword: boolean;
  onClose: () => void;
  onChanged: (profile: ProfileDTO) => void;
  onNotify: (kind: ToastKind, text: string) => void;
}

/** Gözlü şifre alanı — yazılanı göster/gizle. */
const PasswordField: React.FC<{
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  autoFocus?: boolean;
  onChange: (value: string) => void;
}> = ({ id, label, value, placeholder, autoFocus, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          autoFocus={autoFocus}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(event) => onChange(event.target.value)}
          className="pr-9"
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? t('Şifreyi gizle', 'Hide password') : t('Şifreyi göster', 'Show password')}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  );
};

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  hasPassword,
  onClose,
  onChanged,
  onNotify,
}) => {
  const { t: translate } = useLanguage();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Pencere her açılışta boş başlasın — şifre ekranda asılı kalmasın.
  useEffect(() => {
    if (!open) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  }, [open]);

  const rules = checkPasswordRules(newPassword);
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const canSubmit =
    isPasswordStrong(newPassword) &&
    newPassword === confirmPassword &&
    (!hasPassword || currentPassword.length > 0) &&
    !isSaving;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setIsSaving(true);
    setError(null);
    try {
      const profile = await window.zabit.changePassword({
        currentPassword: hasPassword ? currentPassword : undefined,
        newPassword,
      });
      onChanged(profile);
      onNotify(
        'success',
        hasPassword
          ? translate('Şifren güncellendi.', 'Your password has been updated.')
          : translate('Şifren belirlendi.', 'Your password has been set.')
      );
      onClose();
    } catch (err) {
      setError((err as Error).message || translate('Şifre değiştirilemedi.', 'Could not change the password.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={hasPassword ? translate('Şifre Değiştir', 'Change password') : translate('Şifre Belirle', 'Set a password')}
      description={
        hasPassword
          ? undefined
          : translate(
              'Masaüstünde Google ile giriş yapıyorsun; şifre web panelinde e-postayla giriş için kullanılır.',
              'You sign in with Google here; a password lets you sign in with your email on the web panel.'
            )
      }
      className="max-w-md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {translate('Vazgeç', 'Cancel')}
          </Button>
          <Button type="submit" form="change-password-form" disabled={!canSubmit}>
            {isSaving && <Loader2 className="size-4 animate-spin" />}
            {translate('Güncelle', 'Update')}
          </Button>
        </>
      }
    >
      <form id="change-password-form" onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
        {hasPassword && (
          <PasswordField
            id="current-password"
            label={translate('Mevcut şifre', 'Current password')}
            value={currentPassword}
            autoFocus
            onChange={setCurrentPassword}
          />
        )}

        <div className="space-y-2">
          <PasswordField
            id="new-password"
            label={translate('Yeni şifre', 'New password')}
            value={newPassword}
            placeholder={translate('Yeni şifre', 'New password')}
            autoFocus={!hasPassword}
            onChange={setNewPassword}
          />
          <ul className="space-y-1">
            {PASSWORD_RULE_IDS.map((rule) => {
              const ok = rules[rule];
              return (
                <li
                  key={rule}
                  className={cn('flex items-center gap-2 text-[0.8rem]', ok ? 'text-emerald-500' : 'text-muted-foreground')}
                >
                  {ok ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                  {passwordRuleLabel(rule)}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="space-y-2">
          <PasswordField
            id="confirm-password"
            label={translate('Yeni şifre (tekrar)', 'Confirm new password')}
            value={confirmPassword}
            placeholder={translate('Yeni şifreyi tekrar yaz', 'Confirm new password')}
            onChange={setConfirmPassword}
          />
          {mismatch && (
            <p role="alert" className="text-[0.8rem] font-medium text-destructive">
              {translate('Şifreler eşleşmiyor.', 'The passwords do not match.')}
            </p>
          )}
        </div>

        {error && (
          <p role="alert" className="text-[0.8rem] font-medium text-destructive">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
};
